import React, { useMemo, useState, useRef } from 'react';
import { Copy, Check, Download, ClipboardPaste, Upload, Mic, Trash2, Users, Pencil, Replace } from 'lucide-react';
import { exportToDocx, exportToFile, exportToTxt } from './ExportUtils';
import { buildDownloadName } from '../../utils/downloadName';
import { fileToText } from './fileToText';
import { PASTED_SOURCE_NAME } from '../../lib/manualText';
import { parseTranscriptText } from '../../lib/transcriptParse';
import { transcriptToMarkdown } from '../../lib/transcriptMarkdown';
import TranscriptView from '../transcript/TranscriptView';
import MoreFormatsMenu from '../transcript/MoreFormatsMenu';
import {
  EXPORT_FORMATS, NO_TIMES_HINT, buildExport, canSubtitle, segmentsFromText,
} from '../../lib/transcriptExport';
import { createHighlighter } from '../../utils/searchHighlight';
import { withHits } from '../searchHits';

export default function TranscriptionTab({ text, rawText, sourceFileName, durationSec, onManualText, onClear, canEditSpeakers, onEditSpeakers, recognizedLabels, highlight, focusAt, onEditText, onFindReplace, onGoToRecognition }) {
  const [copyState, setCopyState] = useState(null); // null | 'done' | 'failed'
  const [showTimestamps, setShowTimestamps] = useState(true);
  const [inputError, setInputError] = useState(null);
  const fileInputRef = useRef(null);
  const hasText = !!text;
  const hasRaw = !!rawText && rawText !== text;

  const displayText = (showTimestamps && hasRaw) ? rawText : text;
  // Тот же разбор и тот же вид, что на вкладке транскрибации: до 22.08 здесь
  // текст выводился как есть, одной серой стеной с метками внутри строки.
  const fragments = parseTranscriptText(displayText);

  // Другие форматы — за стрелкой у TXT (обращение 6f371de1). Время реплик для
  // субтитров и JSON — всегда из сырого текста: выключенные на экране метки
  // времени не должны лишать файл времени. Markdown — как DOCX, по экрану.
  const timedText = hasRaw ? rawText : text;
  const subtitlesOk = useMemo(() => canSubtitle(segmentsFromText(timedText)), [timedText]);
  const formats = EXPORT_FORMATS.map((f) => ({ ...f, disabled: f.needsTimes && !subtitlesOk }));
  const handleExport = (key) => {
    if (!hasText) return;
    const file = buildExport(key, {
      text: key === 'md' ? displayText : timedText,
      title: sourceFileName || null, durationSec: durationSec || null, showTimecodes: showTimestamps,
    });
    exportToFile(file.content, buildDownloadName(sourceFileName, 'trnscr', file.ext), file.mime);
  };

  const handlePaste = async () => {
    setInputError(null);
    try {
      const clip = await navigator.clipboard.readText();
      if (!clip || !clip.trim()) {
        setInputError('Буфер обмена пуст');
        return;
      }
      onManualText?.(clip, PASTED_SOURCE_NAME);
    } catch {
      setInputError('Не удалось прочитать буфер обмена');
    }
  };

  const handleFile = async (e) => {
    setInputError(null);
    const file = e.target.files?.[0];
    if (fileInputRef.current) fileInputRef.current.value = '';
    if (!file) return;
    try {
      const content = await fileToText(file);
      if (!content || !content.trim()) {
        setInputError('Файл не содержит текста');
        return;
      }
      onManualText?.(content, file.name);
    } catch {
      setInputError('Не удалось прочитать файл');
    }
  };

  // Отказ буфера (нет разрешения, окно не в фокусе, старый браузер) — подпись
  // «Не скопировалось», а не необработанное исключение из обработчика.
  const handleCopy = async () => {
    if (!displayText) return;
    let next = 'done';
    try {
      await navigator.clipboard.writeText(displayText);
    } catch {
      next = 'failed';
    }
    setCopyState(next);
    setTimeout(() => setCopyState(null), 1500);
  };
  const copied = copyState === 'done';
  const copyLabel = copied ? 'Скопировано' : copyState === 'failed' ? 'Не скопировалось' : 'Копировать';

  const disabledBtn = 'opacity-40 cursor-not-allowed';

  return (
    <div className="flex flex-col h-full">
      {/* Телефон: кнопки одной строкой, лишние уезжают вбок — тремя строками
          панель съедала треть экрана над текстом (тест владельца 27.09). */}
      <div className="kbd-hide flex items-center justify-between gap-2 py-2 border-b border-paper-line">
        <span className="hidden md:inline font-mono text-[11px] uppercase tracking-[0.1em] text-paper-ink-3">Исходный текст</span>
        <div className="flex flex-nowrap items-center gap-2 min-w-0 overflow-x-auto md:overflow-visible [&>*]:shrink-0">
          {hasRaw && (
            <label className="flex items-center gap-1.5 cursor-pointer select-none">
              <input
                type="checkbox"
                checked={showTimestamps}
                onChange={(e) => setShowTimestamps(e.target.checked)}
                className="w-3.5 h-3.5 rounded text-paper-accent focus:ring-paper-accent"
              />
              <span className="text-xs text-paper-ink-3">Метки времени</span>
            </label>
          )}
          {canEditSpeakers && (
            <button
              onClick={onEditSpeakers}
              className="flex items-center gap-1 rounded-[8px] bg-paper-bg px-[10px] py-[5px] text-[12px] text-paper-ink hover:bg-paper-panel"
            >
              <Users className="w-3.5 h-3.5" /> Переименовать участников
            </button>
          )}
          {/* Правка текста — только там, где её есть куда сохранить (запись Истории). */}
          {hasText && onEditText && (
            <button
              onClick={onEditText}
              className="flex items-center gap-1 rounded-[8px] bg-paper-bg px-[10px] py-[5px] text-[12px] text-paper-ink hover:bg-paper-panel"
            >
              <Pencil className="w-3.5 h-3.5" /> <span>Править</span>
            </button>
          )}
          {hasText && onFindReplace && (
            <button
              onClick={onFindReplace}
              className="flex items-center gap-1 rounded-[8px] bg-paper-bg px-[10px] py-[5px] text-[12px] text-paper-ink hover:bg-paper-panel"
            >
              <Replace className="w-3.5 h-3.5" /> <span>Найти и заменить</span>
            </button>
          )}
          <button
            onClick={handleCopy}
            disabled={!hasText}
            className={`flex items-center gap-1 rounded-[8px] bg-paper-bg px-[10px] py-[5px] text-[12px] text-paper-ink hover:bg-paper-panel ${!hasText ? disabledBtn : ''}`}
          >
            {copied ? <Check className="w-3.5 h-3.5 text-paper-success" /> : <Copy className="w-3.5 h-3.5" />}
            {/* Подпись в <span> по той же причине, что в ProtocolTab: голый
                текстовый узел рядом со сменяемой иконкой ломает коммит React,
                когда расширение браузера обернуло текст своим элементом. */}
            <span>{copyLabel}</span>
          </button>
          {hasText && onClear && (
            <button
              onClick={() => { if (window.confirm('Очистить текст?')) { setInputError(null); onClear(); } }}
              className="flex items-center gap-1 rounded-[8px] bg-paper-bg px-[10px] py-[5px] text-[12px] text-paper-ink-3 hover:text-paper-accent hover:bg-paper-panel"
            >
              <Trash2 className="w-3.5 h-3.5" /> Очистить
            </button>
          )}
          <MoreFormatsMenu items={formats} onPick={handleExport} disabledHint={NO_TIMES_HINT}
            disabled={!hasText}
            chevronClassName={`rounded-[8px] rounded-l-none bg-paper-bg py-[5px] text-paper-ink hover:bg-paper-panel ${!hasText ? disabledBtn : ''}`}>
            <button
              onClick={() => hasText && exportToTxt(displayText, buildDownloadName(sourceFileName, 'trnscr', 'txt'))}
              disabled={!hasText}
              className={`flex items-center gap-1 rounded-[8px] rounded-r-none bg-paper-bg px-[10px] py-[5px] text-[12px] text-paper-ink hover:bg-paper-panel ${!hasText ? disabledBtn : ''}`}
            >
              <Download className="w-3.5 h-3.5" /> TXT
            </button>
          </MoreFormatsMenu>
          <button
            onClick={() => hasText && exportToDocx(
              // Не сырой текст, а те же реплики, что на экране: иначе pandoc
              // склеивает разговор в один абзац (16.09.2026).
              transcriptToMarkdown(displayText, { showTimecodes: showTimestamps }),
              buildDownloadName(sourceFileName, 'trnscr', 'docx'))}
            disabled={!hasText}
            className={`flex items-center gap-1 rounded-[8px] bg-paper-bg px-[10px] py-[5px] text-[12px] text-paper-ink hover:bg-paper-panel ${!hasText ? disabledBtn : ''}`}
          >
            <Download className="w-3.5 h-3.5" /> DOCX
          </button>
        </div>
      </div>
      <div className={`flex-1 overflow-y-auto p-4 text-sm text-paper-ink-2 leading-relaxed ${
        fragments ? '' : 'whitespace-pre-wrap'}`}>
        {fragments ? (
          <TranscriptView
            fragments={fragments}
            recognizedLabels={recognizedLabels}
            showTimecodes={showTimestamps}
            highlight={highlight}
            focusAt={focusAt}
          />
        ) : withHits(displayText, highlight ? createHighlighter(highlight) : null) || (
          <div className="flex flex-col items-center justify-center h-full gap-4 text-center">
            <span className="text-paper-hint">Нет текста транскрибации</span>
            <div className="flex items-center gap-3">
              {onGoToRecognition && (
                <button
                  onClick={onGoToRecognition}
                  className="flex items-center gap-1.5 px-3 py-2 text-sm text-paper-ink-2 hover:text-paper-accent rounded-panel bg-paper-card hover:"
                >
                  <Mic className="w-4 h-4" /> Расшифровать запись
                </button>
              )}
              <button
                onClick={handlePaste}
                className="flex items-center gap-1.5 px-3 py-2 text-sm text-paper-ink-2 hover:text-paper-accent rounded-panel bg-paper-card hover:"
              >
                <ClipboardPaste className="w-4 h-4" /> Вставить из буфера
              </button>
              <button
                onClick={() => fileInputRef.current?.click()}
                className="flex items-center gap-1.5 px-3 py-2 text-sm text-paper-ink-2 hover:text-paper-accent rounded-panel bg-paper-card hover:"
              >
                <Upload className="w-4 h-4" /> Загрузить из файла
              </button>
              <input
                ref={fileInputRef}
                type="file"
                accept=".txt,.docx"
                onChange={handleFile}
                className="hidden"
              />
            </div>
            {inputError && <span className="text-xs text-paper-accent">{inputError}</span>}
          </div>
        )}
      </div>
    </div>
  );
}
